import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid var(--color-gray-200);
  text-align: center;
`;

const ModalTitle = styled.h2`
  font-family: var(--font-serif);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-gray-900);
  margin-bottom: 0.5rem;
`;

const StoryTitle = styled.p`
  color: var(--color-gray-600);
  font-size: 0.9rem;
  margin: 0;
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const ShareSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ShareLabel = styled.label`
  font-weight: 600;
  color: var(--color-gray-700);
  font-size: 1rem;
`;

const ShareDescription = styled.p`
  color: var(--color-gray-600);
  font-size: 0.9rem;
  margin: 0 0 1rem 0;
  line-height: 1.5;
`;

const LinkContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const LinkInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 0.9rem;
  background-color: var(--color-gray-50);
  color: var(--color-gray-700);
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px var(--color-primary-light);
  }
`;

const CopyButton = styled.button<{ copied?: boolean }>`
  padding: 0.75rem 1rem;
  border-radius: var(--border-radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid;
  white-space: nowrap;
  
  ${({ copied = false }) =>
    copied
      ? `
          background: var(--color-green-500);
          color: var(--color-white);
          border-color: var(--color-green-500);
        `
      : `
          background: var(--color-primary);
          color: var(--color-white);
          border-color: var(--color-primary);
          
          &:hover:not(:disabled) {
            background: var(--color-primary-dark);
            border-color: var(--color-primary-dark);
          }
        `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ShareOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const ShareOptionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  background: var(--color-white);
  color: var(--color-gray-700);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--color-primary);
    background: var(--color-primary-light);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ShareIcon = styled.span`
  font-size: 1.25rem;
`;

const ModalFooter = styled.div`
  padding: 1rem 2rem 2rem 2rem;
  display: flex;
  justify-content: center;
  border-top: 1px solid var(--color-gray-200);
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid var(--color-gray-300);
  background: var(--color-white);
  color: var(--color-gray-700);
  
  &:hover:not(:disabled) {
    background: var(--color-gray-50);
    border-color: var(--color-gray-400);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  background: var(--color-green-50);
  color: var(--color-green-700);
  padding: 0.75rem;
  border-radius: var(--border-radius-md);
  border: 1px solid var(--color-green-200);
  margin-bottom: 1rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  storyTitle: string;
  storyUrl: string;
}

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  storyTitle,
  storyUrl
}) => {
  const [copied, setCopied] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      setCopied(false);
      setShowSuccess(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(storyUrl);
      setCopied(true);
      setShowSuccess(true);
      
      // Reset copied state after 3 seconds
      setTimeout(() => {
        setCopied(false);
      }, 3000);
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      // Fallback: select the text in the input
      const linkInput = document.getElementById('story-link-input') as HTMLInputElement;
      if (linkInput) {
        linkInput.select();
        linkInput.setSelectionRange(0, 99999); // For mobile devices
      }
    }
  };

  const handleShareVia = (platform: string) => {
    const encodedTitle = encodeURIComponent(`Check out this story: "${storyTitle}"`);
    const encodedUrl = encodeURIComponent(storyUrl);
    
    let shareUrl = '';
    
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'email':
        const emailSubject = encodeURIComponent(`Check out this story: "${storyTitle}"`);
        const emailBody = encodeURIComponent(`I thought you'd enjoy this story:\n\n"${storyTitle}"\n\n${storyUrl}`);
        shareUrl = `mailto:?subject=${emailSubject}&body=${emailBody}`;
        break;
      case 'whatsapp':
        const whatsappText = encodeURIComponent(`Check out this story: "${storyTitle}" ${storyUrl}`);
        shareUrl = `https://wa.me/?text=${whatsappText}`;
        break;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick} onKeyDown={handleKeyDown}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Share Story</ModalTitle>
          <StoryTitle>"{storyTitle}"</StoryTitle>
        </ModalHeader>
        
        <ModalBody>
          {showSuccess && (
            <SuccessMessage>
              ✅ Link copied to clipboard!
            </SuccessMessage>
          )}
          
          <ShareSection>
            <ShareLabel>Story Link</ShareLabel>
            <ShareDescription>
              Copy this link to share the story with others
            </ShareDescription>
            
            <LinkContainer>
              <LinkInput
                id="story-link-input"
                type="text"
                value={storyUrl}
                readOnly
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <CopyButton
                onClick={handleCopyLink}
                copied={copied}
              >
                {copied ? '✅ Copied!' : '📋 Copy'}
              </CopyButton>
            </LinkContainer>
            
            <ShareOptions>
              <ShareLabel style={{ marginTop: '0.5rem' }}>Share via</ShareLabel>
              
              <ShareOptionButton onClick={() => handleShareVia('email')}>
                <ShareIcon>📧</ShareIcon>
                Email
              </ShareOptionButton>
              
              <ShareOptionButton onClick={() => handleShareVia('twitter')}>
                <ShareIcon>🐦</ShareIcon>
                Twitter
              </ShareOptionButton>
              
              <ShareOptionButton onClick={() => handleShareVia('facebook')}>
                <ShareIcon>📘</ShareIcon>
                Facebook
              </ShareOptionButton>
              
              <ShareOptionButton onClick={() => handleShareVia('whatsapp')}>
                <ShareIcon>💬</ShareIcon>
                WhatsApp
              </ShareOptionButton>
            </ShareOptions>
          </ShareSection>
        </ModalBody>
        
        <ModalFooter>
          <Button onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ShareModal;